import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import yted from '@lf/api-client/lfApi';
import { LoadingButton } from '@lf/ui-primitives';
import { readPendingShareToken, writePendingShare } from '../lib/share/pendingShare';
import { useProfile } from '../contexts/ProfileContext';

function LoginPage() {
    const { fetchProfile } = useProfile();
    const isMounted = useRef(true);
    const navigate = useNavigate();

    useEffect(() => {
        isMounted.current = true;
        // Persist ?share=<token> across the /signup hop so the share
        // token survives "Create a student account" → signup → /share.
        const params = new URLSearchParams(window.location.search);
        const shareToken = params.get('share');
        if (shareToken) {
            writePendingShare(shareToken);
        }
        return () => {
            isMounted.current = false;
        };
    }, []);

    const [studentCred, setStudentCred] = useState({ username: '', password: '' });
    const [isStudentLoggingIn, setIsStudentLoggingIn] = useState(false);
    const [loginError, setLoginError] = useState('');

    async function handleStudentLogin(event) {
        if (event)
            event.preventDefault();

        if (!studentCred.username || !studentCred.password) {
            return;
        }

        setIsStudentLoggingIn(true);
        setLoginError('');

        try {
            const response = await yted.post('/auth/login_cred', studentCred);
            if (response.status === 200) {
                if (response.data.success) {
                    await fetchProfile();
                    const token = readPendingShareToken();
                    if (token) {
                        navigate(`/share/${token}`);
                    }
                } else {
                    if (isMounted.current) {
                        setLoginError('Invalid credentials. Please try again.');
                    }
                }
            }
            else {
                if (isMounted.current) {
                    setLoginError('Login failed. Please try again.');
                }
            }
        }
        catch (error) {
            console.error('Login failed: ', error);
            if (isMounted.current) {
                setLoginError('Login failed. Please try again later.');
            }
        }

        if (isMounted.current) {
            setIsStudentLoggingIn(false);
        }
    };

    async function handleParentLogin(event) {
        window.location.href = process.env.REACT_APP_LF_PARENT_URL;
    }

    function handleStudentChange(e) {
        const { name, value } = e.target;
        setStudentCred(prevState => ({ ...prevState, [name]: value }));
    };

    return <div className="flex flex-col sm:flex-row mx-auto max-w-screen-lg gap-4 my-8">
        <div className="sm:w-1/2 flex flex-col items-center bg-white border border-gray-300 rounded-2xl">
            <h2 className="text-lg m-4">Student Login</h2>
            <form onSubmit={handleStudentLogin} className="flex flex-col items-center mb-6">
                <input
                    type="text"
                    className="border border-gray-300 p-1 my-1"
                    name="username"
                    placeholder="Username"
                    value={studentCred.username}
                    onChange={handleStudentChange} />
                <input
                    type="password"
                    className="border border-gray-300 p-1 my-1"
                    name="password"
                    placeholder="Password"
                    value={studentCred.password}
                    onChange={handleStudentChange} />

                {loginError && <p className="text-red-500 text-sm py-2">{loginError}</p>}

                <LoadingButton
                    type="submit"
                    className="bg-blue-500 text-white py-2 px-4 rounded my-2"
                    text="Login"
                    isLoading={isStudentLoggingIn}
                    onClick={handleStudentLogin} />

                <a href="/signup" className="text-blue-500 underline text-sm mt-4">
                    Create a student account
                </a>
            </form>
        </div>
        <div className="sm:w-1/2 flex flex-col items-center bg-white border border-gray-300 rounded-2xl">
            <h2 className="text-lg m-4">Parent Login</h2>
            <button className="bg-blue-500 text-white py-2 px-4 rounded my-4"
                onClick={handleParentLogin}>
                Go to parent portal
            </button>
        </div>
    </div>;
}

export default LoginPage;
